import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProperty } from "@/context/PropertyContext";
import api from "@/lib/api";
import { eur, fechaCorta } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Wallet, AlertTriangle, TrendingUp, DoorOpen, CheckCircle2, Trophy,
} from "lucide-react";

// Vista agregada de todos los pisos del usuario: los mismos datos que el
// Panel por piso, pero pidiendo /dashboard sin property_id (el backend ya
// suma todo lo accesible por el usuario en ese caso). Se llega aquí
// pulsando el logo "RentaPiso" del lateral, no tiene entrada propia en el
// menú de navegación.
export default function Resumen() {
  const { selectProperty, properties } = useProperty();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ranking, setRanking] = useState([]);
  const [rankingLoading, setRankingLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/dashboard").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  // Comparativa de rentabilidad por piso: el agregado de arriba no desglosa
  // por piso, así que se pide /dashboard una vez por piso (son pocos). El
  // balance acumulado sin más favorece siempre al piso más antiguo (más años
  // sumando), así que se normaliza por los años con actividad registrada
  // (serie_anual, que el backend ya calcula) para comparar rendimiento medio
  // por año y habitación, no solo quién lleva más tiempo funcionando.
  useEffect(() => {
    if (!properties.length) return;
    setRankingLoading(true);
    Promise.all(
      properties.map((p) =>
        api.get(`/dashboard?property_id=${p.id}`).then((r) => ({
          propertyId: p.id,
          nombre: p.nombre,
          ingresos: r.data.ingresos || 0,
          balance: r.data.balance || 0,
          totalHabitaciones: r.data.total_habitaciones || 0,
          ocupadas: r.data.contratos_activos || 0,
          aniosActivo: (r.data.serie_anual || []).length || 1,
        }))
      )
    )
      .then((rows) => {
        const withRentabilidad = rows.map((r) => ({
          ...r,
          rentabilidadAnual: r.totalHabitaciones > 0 ? (r.balance / r.aniosActivo) / r.totalHabitaciones : 0,
        }));
        withRentabilidad.sort((a, b) => b.rentabilidadAnual - a.rentabilidadAnual);
        setRanking(withRentabilidad);
      })
      .finally(() => setRankingLoading(false));
  }, [properties]);

  const goToPiso = (propertyId) => {
    selectProperty(propertyId);
    navigate("/dashboard");
  };

  if (loading || !data) {
    return (
      <div>
        <PageHeader title="Resumen general" subtitle="Todos tus pisos" />
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-md" />
          ))}
        </div>
        <Skeleton className="h-64 mt-6 rounded-md" />
      </div>
    );
  }

  if ((data.num_pisos || 0) === 0) {
    return (
      <div>
        <PageHeader title="Resumen general" subtitle="Todos tus pisos" />
        <div className="data-card p-10 text-center text-muted-foreground">
          No hay pisos todavía. Crea uno en la sección <strong>Pisos</strong> para empezar.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="resumen-page">
      <PageHeader title="Resumen general" subtitle={`${data.num_pisos} piso(s)`} />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
        <StatCard label="Cobrado" value={eur(data.ingresos)} icon={Wallet} accent="success" testid="resumen-kpi-cobrado" />
        <StatCard
          label="Cobros pendientes"
          value={eur(data.total_pendiente)}
          icon={AlertTriangle}
          accent="warning"
          sub={`${data.pendientes.length} pago(s) vencido(s)`}
          testid="resumen-kpi-pendientes"
        />
        <StatCard
          label="Balance operativo"
          value={eur(data.balance)}
          icon={TrendingUp}
          accent={data.balance >= 0 ? "success" : "danger"}
          sub="Ingresos − suministros"
          testid="resumen-kpi-balance"
        />
        <StatCard
          label="Ocupación"
          value={`${data.contratos_activos} / ${data.total_habitaciones || "—"}`}
          icon={DoorOpen}
          accent="primary"
          sub={`${data.num_inquilinos} inquilino(s) registrado(s)`}
          testid="resumen-kpi-ocupacion"
        />
      </div>

      <div className="data-card p-5 mb-4" data-testid="resumen-alerts-card">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="h-4.5 w-4.5 text-warning" />
          <h3 className="font-display font-medium text-lg">Alertas de gestión</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {(data.alertas || []).map((a) => (
            <div
              key={a.tipo}
              className={`rounded-md border p-3 ${a.nivel === "ok" ? "border-success/20 bg-success/5" : a.nivel === "warning" ? "border-warning/25 bg-warning/5" : "border-border bg-secondary/30"}`}
            >
              <div className="flex items-start gap-2">
                {a.nivel === "ok" ? <CheckCircle2 className="h-4 w-4 text-success mt-0.5" /> : <AlertTriangle className={`h-4 w-4 mt-0.5 ${a.nivel === "warning" ? "text-warning" : "text-primary"}`} />}
                <p className="text-sm font-medium leading-snug">{a.texto}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="data-card p-5 mb-4" data-testid="resumen-ranking-card">
        <div className="flex items-center gap-2 mb-1">
          <Trophy className="h-4.5 w-4.5 text-primary" />
          <h3 className="font-display font-medium text-lg">Rentabilidad por piso</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Balance medio por año y habitación — normalizado por los años con actividad de cada piso, para que uno con más años de histórico no gane solo por acumular más tiempo.
        </p>
        {rankingLoading ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10 rounded-md" />)}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="resumen-ranking-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">#</TableHead>
                  <TableHead>Piso</TableHead>
                  <TableHead className="text-right">Ocupación</TableHead>
                  <TableHead className="text-right">Años activo</TableHead>
                  <TableHead className="text-right">Ingresos (total)</TableHead>
                  <TableHead className="text-right">Balance (total)</TableHead>
                  <TableHead className="text-right">€/hab/año</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ranking.map((p, idx) => (
                  <TableRow
                    key={p.propertyId}
                    onClick={() => goToPiso(p.propertyId)}
                    className="cursor-pointer hover:bg-secondary/40"
                    data-testid={`resumen-ranking-${p.propertyId}`}
                  >
                    <TableCell className="font-mono text-xs text-muted-foreground">{idx + 1}</TableCell>
                    <TableCell className="font-medium">{p.nombre}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{p.ocupadas} / {p.totalHabitaciones}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{p.aniosActivo}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{eur(p.ingresos)}</TableCell>
                    <TableCell className={`text-right font-mono text-sm ${p.balance >= 0 ? "text-success" : "text-destructive"}`}>{eur(p.balance)}</TableCell>
                    <TableCell className="text-right font-mono text-sm font-semibold">{eur(p.rentabilidadAnual)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <div className="data-card p-5" data-testid="resumen-expirations-card">
        <div className="flex items-center gap-2 mb-4">
          <DoorOpen className="h-4.5 w-4.5 text-primary" />
          <h3 className="font-display font-medium text-lg">Próximos vencimientos</h3>
        </div>
        {(data.contratos_vigencia || []).length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No hay contratos próximos con vencimiento calculable.</p>
        ) : (
          <div className="divide-y divide-border">
            {(data.contratos_vigencia || []).slice(0, 12).map((c) => (
              <div key={c.id} className="flex items-center justify-between py-2.5">
                <div>
                  <p className="text-sm font-medium">{c.tenant_nombre}{c.habitacion ? ` · Hab. ${c.habitacion}` : ""}</p>
                  <p className="text-xs text-muted-foreground">
                    {c.property_nombre}{c.property_nombre ? " · " : ""}
                    {c.fecha_fin_tipo === "inicial" ? "Fin inicial" : "Fin"}: {fechaCorta(c.fecha_fin)}
                  </p>
                </div>
                <span className={`font-mono text-sm font-semibold ${c.dias_restantes <= 30 ? "text-warning" : "text-muted-foreground"}`}>
                  {typeof c.dias_restantes === "number" ? `${c.dias_restantes} d` : "—"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
